// filepath: /C:/coding/skills-app/skills-app/src/HoggedInPage.js
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAppAuth } from './auth/AppAuthProvider';
import { Box, Flex, Text, VStack, Spinner, SimpleGrid } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import useSkillsData from './hooks/useSkillsData';
import { usePeopleApi } from './usePeopleApi';
import { FAST_API_BASE_PEOPLE } from './config';
import SummarySection from './components/hoggedin/SummarySection';
import RoleTitleSection from './components/hoggedin/RoleTitleSection';
import { mergeSuggestedRole } from './utils/roleTitle';
import { ANALYTICS_EVENTS, trackEvent } from './analytics';
import ExperienceSection from './components/hoggedin/ExperienceSection';
import SkillsSection from './components/hoggedin/SkillsSection';
import SkillHistoryChart from './components/hoggedin/SkillHistoryChart';
import CompaniesSection from './components/hoggedin/CompaniesSection';
import ProfileHeader from './components/hoggedin/ProfileHeader';
import AdminToolbar from './components/hoggedin/AdminToolbar';
import EditControls from './components/hoggedin/EditControls';
import usePersonProfile from './hooks/usePersonProfile';
import useEditableProfile from './hooks/useEditableProfile';
import useProfileSave from './hooks/useProfileSave';
import LongTextModal from './components/hoggedin/LongTextModal';

const HoggedInPage = () => {
  const { accessToken, isAdmin } = useAppAuth();
  const searchParams = new URLSearchParams(window.location.search);
  const personIdParam = searchParams.get('personid');
  const shouldOpenInEditMode = searchParams.get('edit') === '1';
  const navigate = useNavigate();
  const hasAutoStartedEditingRef = useRef(false);

  const { skills: selectableSkills, isSkillsLoading, skillsError, fetchSkills } = useSkillsData(accessToken);
  const {
    creatingProfile,
    exportLinks,
    exporting,
    handleCreateProfile,
    handleExportProfile,
    actionErrors
  } = usePeopleApi(accessToken);

  const {
    person,
    loading,
    error,
    resolvedPersonId,
    logoSrc,
    backgroundColor,
    skillSnapshot,
    initialCompanies,
    refreshPerson
  } = usePersonProfile(accessToken, personIdParam);

  const {
    isEditing,
    setIsEditing,
    editedDescription,
    setEditedDescription,
    editedExperience,
    setEditedExperience,
    editedRole,
    setEditedRole,
    editableSkills,
    companyEntries,
    startEditing,
    cancelEditing,
    handleLevelChange,
    handleCompanyFieldChange,
    handleCompanyAdd,
    handleCompanyRemove,
    buildPayload,
    isSaving,
    saveError,
    setSaveError,
    setIsSaving
  } = useEditableProfile(person, skillSnapshot, initialCompanies);

  const [isTextAssisting, setIsTextAssisting] = useState(false);
  const [textAssistError, setTextAssistError] = useState(null);

  const onLevelChange = useCallback(
    (skillId, nextValue) => handleLevelChange(skillId, nextValue, selectableSkills),
    [handleLevelChange, selectableSkills]
  );

  const handleStartEditing = useCallback(() => {
    if (isEditing || !person) {
      return;
    }
    const companySource = person?.companies ?? initialCompanies;
    startEditing(person, skillSnapshot, companySource);
    trackEvent(ANALYTICS_EVENTS.EDIT_STARTED);
  }, [isEditing, person, initialCompanies, skillSnapshot, startEditing]);

  const handleFineTuneProfile = useCallback(
    (personId, currentPerson, options = {}) => {
      handleCreateProfile(personId, currentPerson, {
        ...options,
        onProfileReady: refreshPerson
      });
    },
    [handleCreateProfile, refreshPerson]
  );

  useEffect(() => {
    if (!shouldOpenInEditMode || hasAutoStartedEditingRef.current || loading || isEditing || !person) {
      return;
    }

    const companySource = person?.companies ?? initialCompanies;
    startEditing(person, skillSnapshot, companySource);
    hasAutoStartedEditingRef.current = true;
  }, [shouldOpenInEditMode, loading, isEditing, person, initialCompanies, skillSnapshot, startEditing]);

  const handleCancelEdit = useCallback(() => {
    if (!person) {
      return;
    }
    const companySource = person?.companies ?? initialCompanies;
    cancelEditing(person, skillSnapshot, companySource);
  }, [person, initialCompanies, skillSnapshot, cancelEditing]);

  const canRequestTextAssist = Boolean(editedDescription?.trim()) && Boolean(editedExperience?.trim());

  const handleTextAssist = useCallback(async () => {
    if (!accessToken || !resolvedPersonId) {
      return;
    }

    setIsTextAssisting(true);
    setTextAssistError(null);

    try {
      const response = await fetch(`${FAST_API_BASE_PEOPLE}/${resolvedPersonId}/textassistant`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          description: editedDescription ?? '',
          experience: editedExperience ?? ''
        })
      });

      let parsedBody = null;
      try {
        parsedBody = await response.json();
      } catch (_) {
        parsedBody = null;
      }

      if (!response.ok) {
        let message = 'Failed to get text assistance';
        if (typeof parsedBody?.detail === 'string') {
          message = parsedBody.detail;
        } else if (Array.isArray(parsedBody?.detail) && parsedBody.detail[0]?.msg) {
          message = parsedBody.detail[0].msg;
        }
        throw new Error(message);
      }

      setEditedDescription(parsedBody?.description ?? '');
      setEditedExperience(parsedBody?.experience ?? '');
      setEditedRole((current) => mergeSuggestedRole(current, parsedBody?.role));
    } catch (err) {
      setTextAssistError(err.message || 'Failed to get text assistance');
    } finally {
      setIsTextAssisting(false);
    }
  }, [
    accessToken,
    resolvedPersonId,
    editedDescription,
    editedExperience,
    setEditedDescription,
    setEditedExperience,
    setEditedRole
  ]);

  const {
    requestSave: handleSaveEdit,
    companiesNotice,
    clearCompaniesNotice,
    isLongTextPromptOpen,
    confirmSaveAnyway,
    chooseSummarise,
    cancelLongText
  } = useProfileSave({
    person,
    accessToken,
    resolvedPersonId,
    buildPayload,
    editedDescription,
    editedExperience,
    setIsSaving,
    setSaveError,
    setIsEditing,
    refreshPerson,
    onSummarise: handleTextAssist
  });

  useEffect(() => {
    if (isEditing) {
      clearCompaniesNotice();
    }
  }, [isEditing, clearCompaniesNotice]);

  if (loading) {
    return (
      <Flex justify="center" align="center" minH="40vh">
        <Spinner size="lg" color="blue.500" thickness="4px" speed="0.6s" />
      </Flex>
    );
  }

  if (error) {
    return <Text color="red.500">Error: {error}</Text>;
  }

  if (!person) {
    return <Text color="red.500">Error: Person record unavailable.</Text>;
  }

  return (
    <Box bg={backgroundColor} minH="100vh" py={8} px={4}>
      <Flex justify="center" mb={8}>
        <Box w={{ base: '100%', md: '80%' }}>
          <ProfileHeader name={person.name} logoSrc={logoSrc} onBack={() => navigate(-1)} />

          {isAdmin ? (
            <AdminToolbar
              isEditing={isEditing}
              isSaving={isSaving}
              person={person}
              creatingProfile={creatingProfile}
              handleFineTuneProfile={handleFineTuneProfile}
              exportLinks={exportLinks}
              handleExportProfile={handleExportProfile}
              exporting={exporting}
              actionErrors={actionErrors}
              onStartEdit={handleStartEditing}
              onCancelEdit={handleCancelEdit}
              onSaveEdit={handleSaveEdit}
              onTextAssist={handleTextAssist}
              isTextAssisting={isTextAssisting}
              canRequestTextAssist={canRequestTextAssist}
            />
          ) : (
            <EditControls
              isEditing={isEditing}
              isSaving={isSaving}
              onStartEdit={handleStartEditing}
              onCancelEdit={handleCancelEdit}
              onSaveEdit={handleSaveEdit}
              onTextAssist={handleTextAssist}
              isTextAssisting={isTextAssisting}
              canRequestTextAssist={canRequestTextAssist}
            />
          )}

           {saveError && (
             <Text color="red.500" textAlign="right" mb={2}>
               {saveError}
             </Text>
           )}

           {companiesNotice && (
             <Text
               color={companiesNotice.status === 'warning' ? 'orange.600' : 'gray.600'}
               textAlign="right"
               mb={2}
               role="status"
             >
               {companiesNotice.message}
             </Text>
           )}

           {textAssistError && (
             <Text color="red.500" textAlign="right" mb={2}>
               {textAssistError}
             </Text>
           )}

          <RoleTitleSection
            isEditing={isEditing}
            value={editedRole}
            displayValue={person.role}
            onChange={setEditedRole}
          />

          <SummarySection
            isEditing={isEditing}
            value={editedDescription}
            displayValue={person.description}
            onChange={setEditedDescription}
          />

          {person?.source === 'SSO' && !isEditing && (
            <SkillHistoryChart skillHistory={person?.skillshistory} />
          )}

          {isEditing ? (
            <VStack spacing={6} align="stretch" mb={6}>
              <ExperienceSection
                isEditing
                value={editedExperience}
                displayValue={person.experience}
                onChange={setEditedExperience}
              />
              {person?.source === 'SSO' && (
                <SkillHistoryChart skillHistory={person?.skillshistory} />
              )}
              <SkillsSection
                isEditing
                editableSkills={editableSkills}
                originalSkills={skillSnapshot.list}
                selectableSkills={selectableSkills}
                onLevelChange={onLevelChange}
                isLoading={isSkillsLoading}
                error={skillsError}
                onRetry={fetchSkills}
              />
            </VStack>
          ) : (
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} mb={6}>
              <ExperienceSection
                isEditing={false}
                value={editedExperience}
                displayValue={person.experience}
                onChange={setEditedExperience}
              />
              <SkillsSection
                isEditing={false}
                editableSkills={editableSkills}
                originalSkills={skillSnapshot.list}
                selectableSkills={selectableSkills}
                onLevelChange={onLevelChange}
                isLoading={isSkillsLoading}
                error={skillsError}
                onRetry={fetchSkills}
              />
            </SimpleGrid>
          )}

          <CompaniesSection
            isEditing={isEditing}
            companies={person?.companies}
            entries={companyEntries}
            onFieldChange={handleCompanyFieldChange}
            onAdd={handleCompanyAdd}
            onRemove={handleCompanyRemove}
          />

          {isEditing && (
            <Box mt={4}>
              <EditControls
                isEditing
                isSaving={isSaving}
                onStartEdit={handleStartEditing}
                onCancelEdit={handleCancelEdit}
                onSaveEdit={handleSaveEdit}
              />
            </Box>
          )}

          <LongTextModal
            isOpen={isLongTextPromptOpen}
            canSummarise={canRequestTextAssist}
            onSummarise={chooseSummarise}
            onSaveAnyway={confirmSaveAnyway}
            onCancel={cancelLongText}
          />
        </Box>
      </Flex>
    </Box>
  );
};
export default HoggedInPage;

